import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../store/store';
import { logout, refreshTokenSuccess, refreshTokenFailure } from '../store/slices/authSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Helper to determine if error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  if (!error.response) {
    // Network errors (no response from server)
    return true;
  }

  const status = error.response.status;
  // Retry on 5xx server errors and 429 (rate limit)
  return status >= 500 || status === 429;
};

// Helper to calculate exponential backoff delay
const getRetryDelay = (retryCount: number): number => {
  return Math.min(RETRY_DELAY * Math.pow(2, retryCount), 10000); // Max 10 seconds
};

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 second timeout
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth.accessToken;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 and token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // Initialize retry count
    originalRequest._retryCount = originalRequest._retryCount || 0;

    // Handle retryable errors (network errors, 5xx, 429)
    if (
      isRetryableError(error) &&
      originalRequest._retryCount < MAX_RETRIES &&
      !originalRequest._retry
    ) {
      originalRequest._retryCount += 1;

      // Calculate delay with exponential backoff
      const delay = getRetryDelay(originalRequest._retryCount - 1);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return api(originalRequest);
    }

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const state = store.getState();
      const refreshToken = state.auth.refreshToken;

      if (!refreshToken) {
        store.dispatch(logout());
        processQueue(error, null);
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        // Attempt token refresh
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken
        });

        const { accessToken } = response.data;

        store.dispatch(refreshTokenSuccess({ accessToken }));
        processQueue(null, accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        store.dispatch(refreshTokenFailure());
        processQueue(error, null);
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Estrae il messaggio di errore da una risposta API
 *
 * Gestisce tutti i formati di errore NestJS:
 * 1. Validazione (array): { message: ["errore1", "errore2"], statusCode: 400 }
 * 2. HttpException (stringa): { message: "errore", statusCode: 4xx/5xx }
 * 3. Errore con campo error: { error: "messaggio", statusCode: xxx }
 * 4. Errori di rete: error.message = "Network Error"
 * 5. Errori Axios: error.response.data o error.message
 */
export function getErrorMessage(error: unknown, fallback = 'Si è verificato un errore'): string {
  if (!error) return fallback;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;

  // 1. Prova response.data.message (formato NestJS standard)
  let message = err?.response?.data?.message;

  // 2. Se non c'è message, prova response.data.error
  if (!message && err?.response?.data?.error) {
    message = err.response.data.error;
  }

  // 3. Se non c'è response.data, prova error.message diretto (errori di rete)
  if (!message && err?.message) {
    message = err.message;
  }

  // 4. Se ancora niente, usa il fallback
  if (!message) return fallback;

  // 5. Gestisci array di messaggi (errori di validazione NestJS)
  if (Array.isArray(message)) {
    const filtered = message.filter((m): m is string => typeof m === 'string' && m.trim() !== '');
    return filtered.length > 0 ? filtered.join(', ') : fallback;
  }

  // 6. Converti a stringa e verifica che non sia vuota
  const strMessage = String(message).trim();
  return strMessage || fallback;
}

export default api;
